import {useLoaderData} from 'react-router-dom';
import React, { useState, useEffect, useMemo } from "react";
import { useUser } from "../components/UserContext";
import {getUser, getManagerContent, initialiseBaseSkins, 
    createNew, deleteItemFromDB, regenURL,
    editItemOnDB, moveItemInDB, rearrangeItemsInDB } from '../Users'
import PagesManager from '../components/PagesManager';
import SkinsManager from '../components/SkinsManager';
import ReplyIcon from '@mui/icons-material/Reply';
import HomeIcon from '@mui/icons-material/Home';
import FoldersDisplay from '../components/FoldersDisplay';
import FoldersReorderer from '../components/FoldersReorderer';

export async function loader({params}) {
    const userId = params.userId;
    const currentlyManaging = params.type;
    const pageAuthor = await getUser(userId);
    const {pages, skins} = await getManagerContent(userId);
    if (!pageAuthor) {
        throw new Response("", {
            status: 404,
            statusText: "Not Found",
        });
    }
    return {pageAuthor, pages, skins, currentlyManaging};
}

export default function Manager() {
    const {pages, skins, currentlyManaging, pageAuthor} = useLoaderData();
    const {currentUser} = useUser();
    const [total, setTotal] = useState([]);
    const [target, setTarget] = useState({ path: [], method: null, movingItem: null, editingItem: null });
    const [input, setInput] = useState({
        title: "",
        type: "page",
        skinId: skins?.[0]?.id ?? null,
        isPrivate: 0
    });

    function findLowestSkinId(items) {
        let lowestId = null;

        for (const item of items) {
            if (item.type === "skin") {
                if (lowestId === null || item.id < lowestId) {
                    lowestId = item.id;
                }
            }

            if (item.type === "folder" && item.children?.length) {
                const childLowest = findLowestSkinId(item.children);

                if (childLowest !== null && (lowestId === null || childLowest < lowestId)) {
                    lowestId = childLowest;
                }
            }
        }

        return lowestId;
    }

    const defaultSkin = findLowestSkinId(skins);

    useEffect(() => {
        initialiseBaseSkins(currentUser.id);
        document.title = `${currentlyManaging} manager`;
    }, [currentUser.id, currentlyManaging]);


    useEffect(() => { // initialise
        if (currentlyManaging === "pages") {
            if (pages.length >= 0) {
                setTotal([...pages]);
                setTarget({ path: [], method: null, movingItem: null, editingItem: null });
            }
        } else if ( currentlyManaging === "skins") {
            if (skins.length >= 0) {
                setTotal([...skins]);
                setTarget({ path: [], method: null, movingItem: null, editingItem: null });
            }
        }
    }, [pages, skins, currentlyManaging]);

    useEffect(() => { // skin input?
        if (skins.length > 0 && currentlyManaging === "pages" && input.skinId == null) {
        setInput(prev => ({ ...prev, skinId: skins[0].id }));
        }
    }, [skins, input.skinId, currentlyManaging]);

    const currentlyViewing = useMemo(() => {
        let items = total;

        for (const folderId of target.path) {
            const folder = items.find(item => (item.type === "folder" || item.type === "skinFolder") && item.id === folderId);
            if (!folder) return [];
            items = folder.children || [];
        }
        return items;
    }, [total, target.path]);

    const breadcrumbs = useMemo(() => {
        const crumbs = [];
        let items = total;

        for (const folderId of target.path) {
            const folder = items.find(item => (item.type === "folder" || item.type === "skinFolder") && item.id === folderId);
            if (!folder) break;

            if (currentlyManaging === "pages") {
                crumbs.push({
                    id: folder.id,
                    title: folder.title,
                    skinId: folder.defaultSkinId,
                    isPrivate: folder.isPrivate,
                    type: folder.type
                });
            } else {
                crumbs.push({
                    id: folder.id,
                    title: folder.title,
                    type: folder.type
                });
            }

            items = folder.children || [];
        }

        return crumbs;
    }, [total, target.path, currentlyManaging]);


    function updateTarget(method, updatedItem) {
        if (method === "reorder") {
            if (target.method === "reorder") {
                setTarget((prev) => ({ ...prev, method: null }));
            } else {
                setTarget((prev) => ({ ...prev, method: "reorder" }));
            }
        } else{
            setTarget((prev) => ({ ...prev, method: method }));
        }
    }

    function handleBackClick() {
        if (target.path.length > 0) {
            setTarget(prev => ({
                ...prev,
                path: prev.path.slice(0, target.method === "edit" ? -2 : -1),
                method: null
            }));
        }
    }

    function handleHomeClick() {
        setTarget({ method: null, path: [] });
    }

    function deleteItem(type, id, order) {
        const parentId = target.path.length > 0 ? target.path[target.path.length - 1] : null;

        setTotal(prev => deleteFromTree(prev, target.path, id, type));

        deleteItemFromDB(type, id, order, parentId, currentUser.id, total[0].id);
    }

    async function addNew(newItem) {
        const newItemDetails = await createNew(newItem, currentUser.id);

        const newItemId = newItemDetails.itemId;
        const newPageCode = newItemDetails.randomCode;

        const parentId = target.path.length > 0 ? target.path[target.path.length - 1] : null;

        const createdItem =
            currentlyManaging === "pages" 
                ? newItem.type === "page"
                    ? {
                        id: newItemId,
                        type: "page",
                        title: newItem.title,
                        isPrivate: newItem.isPrivate,
                        order: newItem.order ?? 0,
                        folderId: parentId,
                        skinId: newItem.skinId,
                        isProfile: 0,
                        pageCode: newPageCode,
                        }
                    : {
                        id: newItemId,
                        type: "folder",
                        title: newItem.title,
                        isPrivate: newItem.isPrivate,
                        order: newItem.order ?? 0,
                        parentId,
                        children: [],
                        defaultSkinId: newItem.skinId,
                        }
                : newItem.type === "skin"
                    ? {
                        id: newItemId,
                        type: "skin",
                        authorId: currentUser.id,
                        folderId: parentId,
                        order: newItem.order ?? 0,
                        title: newItem.title, 
                        groupWidth: newItem.groupWidth,
                        columnStart: newItem.columnStart,
                        columnEnd: newItem.columnEnd,
                        pageTitleFont: newItem.pageTitleFont, 
                        pageTitleColor:newItem.pageTitleColor, 
                        pageSubtitleFont: newItem.pageSubtitleFont,
                        pageSubtitleColor: newItem.pageSubtitleColor, 
                        pageBackgroundColor: newItem.pageBackgroundColor,
                        backgroundImage: newItem.backgroundImage,
                        curvedBorders: newItem.curvedBorders, 
                        groupBorders: newItem.groupBorders, 
                        blockBorders: newItem.blockBorders, 
                        borderColor: newItem.borderColor, 
                        titleBorderColor: newItem.titleBorderColor, 
                        groupBackgroundColor: newItem.groupBackgroundColor,
                        blockBackgroundColor: newItem.blockBackgroundColor, 
                        textFont: newItem.textFont, 
                        textSize: newItem.textSize, 
                        textColor: newItem.textColor, 
                        linkColor: newItem.linkColor, 
                        linkHoverColor: newItem.linkHoverColor
                    }
                    : {
                        id: newItemId,
                        type: "skinFolder",
                        title: newItem.title,
                        authorId: currentUser.id,
                        parentId,
                        order: newItem.order ?? 0,
                        children: []
                    }
            ;

        setTotal(prev => insertAtPath(prev, target.path, createdItem));
    }

    function edit(newItem) {
        editItemOnDB(newItem);
        setTotal(prev => updateTree(prev, newItem));

        setTarget(prev => {
            return { 
                ...prev,
                method: null,
                editingItem: null,
            }; 
        });
    }

    function handleRearrange(itemId, itemType, itemOldOrder, itemDesiredOrder) {
    rearrangeItemsInDB(
        currentUser.id,
        itemId,
        itemType,
        itemOldOrder,
        itemDesiredOrder
    );

    setTotal(prev =>
        updateChildrenAtPath(prev, target.path, items =>
        reorderArray(items, itemOldOrder, itemDesiredOrder)
        )
    );
    }

    async function handleRegenURL(id) {
        const { randomCode } = await regenURL(id);

        setTotal(prev =>
            updateItemAtPath(prev, target.path, id, item => ({
            ...item,
            pageCode: randomCode,
            }))
        );
    }

    function moveItem(itemId, itemType, fromPath, toPath) {
        setTotal(prev => {
            const sourceChildren = findChildrenAtPath(prev, fromPath);
            if (!sourceChildren) return prev;

            const movingItem = sourceChildren.find(
                item => item.id === itemId && item.type === itemType
            );

            if (!movingItem) return prev;

            // Remove item first
            const withoutItem = deleteFromTree(
                prev,
                fromPath,
                itemId,
                itemType
            );

            // Find destination AFTER removal
            const destinationChildren = findChildrenAtPath(
                withoutItem,
                toPath
            );

            if (!destinationChildren) return prev;

            const oldOrder = movingItem.order;
            const newOrder = destinationChildren.length;

            const parentId = toPath.length
                ? toPath[toPath.length - 1]
                : null;

            const updatedMovingItem = setParentFields(
                {
                    ...movingItem,
                    order: newOrder
                },
                parentId
            );

            const withItem = insertAtPath(
                withoutItem,
                toPath,
                updatedMovingItem
            );

            moveItemInDB(
                currentUser.id,
                itemId,
                itemType,
                oldOrder,
                newOrder,
                parentId
            );

            return withItem;
        });
    }

    function moveOut(id, type) {
    if (target.path.length === 0) return;

    const fromPath = target.path;
    const toPath = target.path.slice(0, -1);

    moveItem(id, type, fromPath, toPath);
    }

    function moveIn(id, type, destinationFolderPath) {
    const fromPath = target.path;
    const toPath = destinationFolderPath;

    setTarget(prev => ({
        ...prev,   
        method: null,
        movingItem: null
        }));

    moveItem(id, type, fromPath, toPath);
    }


        function cleanOrder(things) {
            return things.map((item, index) => ({
                ...item,
                order: index
            }));
        }

        function updateChildrenAtPath(items, path, updater) {
            if (path.length === 0) {
                return updater(items);
            }

            const [folderId, ...rest] = path;

            return items.map(item => {
                if ((item.type !== "folder" && item.type !== "skinFolder") || item.id !== folderId) return item;

                return {
                ...item,
                children: updateChildrenAtPath(item.children || [], rest, updater),
                };
            });
        }

        function findChildrenAtPath(items, path) {
            let current = items;

            for (const folderId of path) {
                const folder = current.find(item => (item.type === "folder" || item.type === "skinFolder") && item.id === folderId);
                if (!folder) return [];
                current = folder.children || [];
            }

            return current;
        }

        function deleteFromTree(items, path, deleteId, deleteType) {
            return updateChildrenAtPath(items, path, children =>
                cleanOrder(
                    children.filter(
                        item => !(item.id === deleteId && item.type === deleteType)
                    )
                )
            );
        }

        function insertAtPath(items, path, newItem) {
            return updateChildrenAtPath(items, path, children =>
                cleanOrder([...children, newItem])
            );
        }
        
        function setParentFields(item, parentId) {
            if (item.type === "page" || item.type === "skin") {
                return { ...item, folderId: parentId };
            }

            return { ...item, parentId };
        }

        function propagateFolderChanges(children, oldSkinId, newSkinId, newPrivacy, allowSkinPropagation = true) {
        return (children || []).map(child => {
            const skinKey = child.type === "page" ? "skinId" : "defaultSkinId";
            const childSkin = child[skinKey];
            const matchesOldSkin = allowSkinPropagation && childSkin === oldSkinId;

            const updatedChild = {
            ...child,
            isPrivate: newPrivacy,
            };

            if (matchesOldSkin) {
            updatedChild[skinKey] = newSkinId;
            }

            if (child.type === "folder" && child.children?.length) {
            updatedChild.children = propagateFolderChanges(
                child.children,
                oldSkinId,
                newSkinId,
                newPrivacy,
                matchesOldSkin
            );
            }

            return updatedChild;
        });
        }

        function updateItemAtPath(items, path, itemId, updater) {
            if (path.length === 0) {
                return items.map(item => (item.id === itemId ? updater(item) : item));
            }

            const [folderId, ...rest] = path;

            return items.map(item => {
                if ((item.type !== "folder" || item.type !== "skinFolder") || item.id !== folderId) return item;

                return {
                    ...item,
                    children: updateItemAtPath(item.children || [], rest, itemId, updater),
                };
            });
        }

        function updateTree(items, newItem) {
            return items.map(item => {
                if (item.type === newItem.type && item.id === newItem.id) {
                    if (item.type === "page") {
                        return {
                            ...item,
                            title: newItem.title,
                            isPrivate: newItem.isPrivate,
                            skinId: newItem.skinId,
                        };
                    } else if (item.type === "skin") {
                        return {
                            ...item,
                            title: newItem.title, 
                            groupWidth: newItem.groupWidth,
                            columnStart: newItem.columnStart,
                            columnEnd: newItem.columnEnd,
                            pageTitleFont: newItem.pageTitleFont, 
                            pageTitleColor:newItem.pageTitleColor, 
                            pageSubtitleFont: newItem.pageSubtitleFont,
                            pageSubtitleColor: newItem.pageSubtitleColor, 
                            pageBackgroundColor: newItem.pageBackgroundColor,
                            backgroundImage: newItem.backgroundImage,
                            curvedBorders: newItem.curvedBorders, 
                            groupBorders: newItem.groupBorders, 
                            blockBorders: newItem.blockBorders, 
                            borderColor: newItem.borderColor, 
                            titleBorderColor: newItem.titleBorderColor, 
                            groupBackgroundColor: newItem.groupBackgroundColor,
                            blockBackgroundColor: newItem.blockBackgroundColor, 
                            textFont: newItem.textFont, 
                            textSize: newItem.textSize, 
                            textColor: newItem.textColor, 
                            linkColor: newItem.linkColor, 
                            linkHoverColor: newItem.linkHoverColor
                        }
                    } else if (item.type === "skinFolder") {
                        return {
                            ...item,
                            title: newItem.title, 
                        }
                    }

                    const oldSkinId = item.defaultSkinId;

                    return {
                        ...item,
                        title: newItem.title,
                        isPrivate: newItem.isPrivate,
                        defaultSkinId: newItem.skinId,
                        children: propagateFolderChanges(
                        item.children || [],
                        oldSkinId,
                        newItem.skinId,
                        newItem.isPrivate
                        ),
                    };
                }

                if (item.type !== "folder" || !item.children?.length) {
                    return item;
                }

                return {
                    ...item,
                    children: updateTree(item.children, newItem),
                };
            });
        }
        
        function reorderArray(items, oldIndex, newIndex) {
            const updated = [...items];
            const [moved] = updated.splice(oldIndex, 1);
            updated.splice(newIndex, 0, moved);
            return cleanOrder(updated);
        }

    return(
        <div>
            <div className="page-head">
                <div className="page-title settings-title">
                    <div className="creator-pfp" style={{backgroundColor:"rgba(0, 0, 0, 0)"}}>
                    </div>
                    <h2 className="title-text settings-title">{currentlyManaging} manager</h2>
                    <h3>create and manage {currentlyManaging}</h3>
                </div>
            </div>
            <div>
                <div id="folder-toolbar">
                    <div className="folder-explore-tools">
                        <div className="folder-button" onClick={handleHomeClick}><HomeIcon /></div>
                        <div className="folder-button" onClick={handleBackClick}><ReplyIcon /></div>
                        <nav className="folder-navigator">
                            {breadcrumbs.map((crumb, index) => (
                                <span key={crumb.id}>
                                {"/"}
                                <button
                                    onClick={() =>
                                    setTarget(prev => ({
                                        ...prev,
                                        path: prev.path.slice(0, index + 1)
                                    }))
                                    }
                                >
                                    {crumb.title}
                                </button>
                                </span>
                            ))}
                        </nav>
                    </div>
                    <div className="folder-actions">
                        <div onClick={() => {updateTarget("create")}}>create</div>
                        <div id={target.method==="reorder"?"reordering":null} onClick={() => {updateTarget("reorder")}}>reorder</div>
                    </div>
                </div>
                {target.method === "moveIn" && 
                    <div id="folders-display-notice">
                        <p> click on the folder or collection the item should move into </p>
                        <div onClick={handleBackClick}> cancel </div>
                    </div>
                }
                {target.method !== "edit" && target.method !== "create" && target.method !== "reorder" && 
                    <FoldersDisplay 
                        currentlyViewing={currentlyViewing} 
                        currentUser={currentUser} 
                        updateTarget={updateTarget} 
                        setTarget={setTarget} 
                        target={target} 
                        deleteItem={deleteItem} 
                        moveOut={moveOut} 
                        moveIn={moveIn} 
                        type={currentlyManaging}
                        handleRegenURL={handleRegenURL}
                        defaultSkin={defaultSkin}
                        total={skins}
                    />
                }
                {target.method === "reorder" && <FoldersReorderer currentlyViewing={currentlyViewing} handleRearrange={handleRearrange} type={currentlyManaging}/>}
                {currentlyManaging === "pages" && (target.method === "create" || target.method === "edit") && 
                    <PagesManager 
                        pages={total} 
                        skins={skins} 
                        currentUser={currentUser} 
                        breadcrumbs={breadcrumbs}
                        target={target}
                        setTarget={setTarget}
                        addNew={addNew}
                        edit={edit}
                    />
                }
                {currentlyManaging === "skins" && (target.method === "create" || target.method === "edit") && 
                    <SkinsManager   
                        skins={total} 
                        currentUser={currentUser} 
                        pageAuthor={pageAuthor} 
                        breadcrumbs={breadcrumbs}
                        target={target}
                        setTarget={setTarget}
                        addNew={addNew}
                        edit={edit}
                    />
                }
            </div>
        </div>
    )
}